import { Injectable, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  map,
  shareReplay,
  switchMap,
} from 'rxjs';
import {
  ConnectionConsumerManagerService,
  RealtimeServerEvent,
  handleRealtimeServerEventSafely,
} from '@wilson/non-domain-specific/realtime-collaboration/core';
import { excludeCurrentRealtimeViewer } from '../utils/realtime-presence.utils';
import { UserColorService } from './user-color.service';
import { User } from '@wilson/interfaces';
import { NonAccessibleUser } from '../utils/types';
import { UsersState } from '@wilson/account';

@Injectable({ providedIn: 'root' })
export class TimelinePresenceService implements OnDestroy {
  private static readonly CONSUMER_ID = 'timeline';

  private readonly connectionConsumerManagerService = inject(
    ConnectionConsumerManagerService,
  );
  private readonly store = inject(Store);
  private readonly userColorService = inject(UserColorService);

  private readonly shiftRealtimeViewersByIdSubject = new BehaviorSubject<
    Record<string, Set<string>>
  >({});

  private readonly usersMap$ = UsersState.ensureUsersLoaded(this.store).pipe(
    switchMap(() => this.store.select(UsersState.usersMap)),
  );

  private readonly shiftViewersById$: Observable<
    Record<string, Array<User | NonAccessibleUser>>
  > = combineLatest([
    this.shiftRealtimeViewersByIdSubject,
    this.usersMap$,
  ]).pipe(
    map(([viewerIdsByShiftId, usersMap]) =>
      Object.fromEntries(
        Object.entries(viewerIdsByShiftId).map(([shiftId, viewerIds]) => [
          shiftId,
          Array.from(viewerIds).map(
            (id) => usersMap[id] ?? 'non-accessible-user',
          ),
        ]),
      ),
    ),
  );

  private isRegistered = false;
  private activeShiftId: string | null = null;
  private incomingSubscription?: Subscription;

  readonly shiftViewerBorderColorById$: Observable<Record<string, string>> =
    combineLatest([
      this.shiftViewersById$,
      this.store.select(AuthState.userId),
    ]).pipe(
      map(([viewersById, currentUserId]) => {
        const borderColorByShiftId: Record<string, string> = {};

        for (const [shiftId, viewers] of Object.entries(viewersById)) {
          const filteredViewers = excludeCurrentRealtimeViewer(
            viewers,
            currentUserId,
          );

          if (filteredViewers.length > 0) {
            const firstViewer = filteredViewers[0];
            if (firstViewer !== 'non-accessible-user' && firstViewer.id) {
              borderColorByShiftId[shiftId] =
                this.userColorService.getColorForUser(firstViewer.id);
            }
          }
        }

        return borderColorByShiftId;
      }),
      shareReplay(1),
    );

  readonly isEnabled$ = this.connectionConsumerManagerService.isEnabled$;

  ngOnDestroy(): void {
    this.unregister();
    this.shiftRealtimeViewersByIdSubject.complete();
  }

  register(): void {
    if (this.isRegistered) {
      return;
    }

    this.isRegistered = true;
    this.incomingSubscription =
      this.connectionConsumerManagerService.incomingActions$.subscribe(
        (event) =>
          handleRealtimeServerEventSafely({
            event,
            handler: (serverEvent) => this.handleServerEvent(serverEvent),
            context: TimelinePresenceService.CONSUMER_ID,
          }),
      );
    this.connectionConsumerManagerService.registerConsumer({
      consumerId: TimelinePresenceService.CONSUMER_ID,
      onReconnect: () => this.resubscribeActiveShift(),
      onSuspend: () => this.clearPresenceState(),
    });
  }

  unregister(): void {
    if (!this.isRegistered) {
      return;
    }

    if (this.activeShiftId) {
      this.closeActiveShift();
    }

    this.connectionConsumerManagerService.removeConsumer(
      TimelinePresenceService.CONSUMER_ID,
    );
    this.incomingSubscription?.unsubscribe();
    this.incomingSubscription = undefined;
    this.clearPresenceState();
    this.isRegistered = false;
  }

  openShift(newShiftId: string): void {
    if (
      !newShiftId ||
      !this.isRegistered ||
      this.activeShiftId === newShiftId
    ) {
      return;
    }

    if (this.activeShiftId) {
      this.connectionConsumerManagerService.send({
        action: 'shift/unview',
        shiftId: this.activeShiftId,
      });
    }

    this.activeShiftId = newShiftId;
    this.connectionConsumerManagerService.send({
      action: 'shift/view',
      shiftId: newShiftId,
    });
  }

  closeActiveShift(): void {
    if (!this.activeShiftId) {
      return;
    }

    const shiftId = this.activeShiftId;
    this.connectionConsumerManagerService.send({
      action: 'shift/unview',
      shiftId,
    });
    this.activeShiftId = null;
    this.clearShiftViewers(shiftId);
  }

  getShiftViewers(
    shiftId: string,
  ): Observable<Array<User | NonAccessibleUser>> {
    return combineLatest([
      this.shiftViewersById$,
      this.store.select(AuthState.userId),
    ]).pipe(
      map(([viewersById, currentUserId]) =>
        excludeCurrentRealtimeViewer(viewersById[shiftId] ?? [], currentUserId),
      ),
    );
  }

  private resubscribeActiveShift(): void {
    if (this.activeShiftId) {
      this.connectionConsumerManagerService.send({
        action: 'shift/view',
        shiftId: this.activeShiftId,
      });
    }
  }

  private handleServerEvent(event: RealtimeServerEvent): void {
    if (event.eventType === 'shift/viewers') {
      this.shiftRealtimeViewersByIdSubject.next({
        ...this.shiftRealtimeViewersByIdSubject.value,
        [event.shiftId]: new Set(event.userIds),
      });
    }
  }

  private clearPresenceState(): void {
    this.shiftRealtimeViewersByIdSubject.next({});
  }

  private clearShiftViewers(shiftId: string): void {
    const next = { ...this.shiftRealtimeViewersByIdSubject.value };
    delete next[shiftId];
    this.shiftRealtimeViewersByIdSubject.next(next);
  }
}
